/**
 * Created by Vishnu Todwal on 2023-08-18.
 */

import {Linear, TimelineLite} from "gsap";
import {IndividualWinView} from "./IndividualWinView";
import {DDay} from "../../DDay";
import {IBetWayWinData} from "../../server/data/IBlitzKriegData";
import {BlitzkriegSymbols, BlitzKriegSymbolState} from "../../symbol/DDaySymbolConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {BlitzKriegParsedGameData} from "../../server/data/BlitzKriegParsedGameData";
import {DDaySymbol} from "../../symbol/DDaySymbol";
import {DDayGameConfig} from "../../DDayGameConfig";
import {BlitzKriegGameMode} from "../../server/BlitzKriegGameMode";

export class IndividualWinController {

    private _view: IndividualWinView;
    private _currentTimeline: TimelineLite | null;
    private _stopped: boolean;

    constructor() {
        this._view = new IndividualWinView();
    }

    public start(): void {
        this._stopped = false;
        if (DDay.model.parsedGameData.betWins.length > 0 && DDay.model.parsedGameData.singleWin > 0 && !DDay.isActionSpinsActive) {
            // Add a small delay for the first time so we don't start to play before the autoPlay start next spin.
            this.playNextBetWin(0, true);
        }
    }

    private playNextBetWin(index: number = 0, isStart: boolean = false): void {
        // TODO: reset all win state if needed.
        const data: BlitzKriegParsedGameData = DDay.model.parsedGameData;
        if (DDay.isActionSpinsActive) {
            DDay.individualWin.stop();
            return;
        }
        const tl: TimelineLite = new TimelineLite();
        if (isStart) {
            tl.to(this, {duration: 1});
        }
        if (index > data.betWins.length || (index === data.betWins.length && !(DDay.model.parsedGameData.mode === BlitzKriegGameMode.NORMAL))) {
            index = 0;
        }
        if (index === data.betWins.length && DDay.model.parsedGameData.mode === BlitzKriegGameMode.NORMAL) {
            tl.add(this.getFinalBetWayWinAnimation());
        } else {
            console.log("Win Data: ", data.betWins[index]);
            tl.add(this.getBetWayAnimation(data.betWins[index]));
        }
        tl.to(this, {duration: 0.5});
        tl.add(() => {
            if (!this._stopped) {
                this.playNextBetWin(index + 1);
            }
        });
        this._currentTimeline = tl;
    }

    protected getFinalBetWayWinAnimation(): TimelineLite {
        let finalReelWinPosition: boolean[][] = ArrayHelper.initArrayWithValues(DDayGameConfig.REELS_NUM, (reelId => {
            return ArrayHelper.initArrayWithValues(DDayGameConfig.REELS_SYM_COUNT[reelId], () => {
                return false;
            });
        }));
        const tl: TimelineLite = new TimelineLite();
        let symbolDelay: number = 0;
        const reelDelay: number = 0;
        DDay.model.parsedGameData.betWins.forEach((reelWinData: IBetWayWinData) => {
            reelWinData.reelWinPositions.forEach((winData: boolean[], reelId: number) => {
                winData.forEach((isWin: boolean, symId: number) => {
                    if (isWin) {
                        finalReelWinPosition[reelId][symId] = isWin;
                    }
                });
            });
        });
        tl.add(() => {
            DDay.initialCountUp.updateWinCount(DDay.model.gameData.accumulatedRoundWin, DDay.model.parsedGameData.totalWays)
        }, 0);
        finalReelWinPosition.forEach((reelWin, reelId) => {
            let hasReelWin: boolean = false;
            reelWin.forEach((winPosition, symId) => {
                if (winPosition) {
                    hasReelWin = true;
                    tl.add(this.getSymbolWinAnimation(reelId, symId), symbolDelay);
                }
            });
            if (hasReelWin) {
                symbolDelay += reelDelay;
            }
        });
        return tl;
    }

    private getBetWayAnimation(winData: IBetWayWinData): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let delay: number = 0;
        tl.add(() => {
            DDay.initialCountUp.updateWinCount(winData.amount, winData.numberOfCombinations)
        }, delay);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            reelWin.forEach((symWin, symId) => {
                if (symWin) {
                    tl.add(this.getSymbolWinAnimation(reelId, symId), 0);
                } else if (DDay.model.parsedGameData.evaluatedArea[reelId][symId] !== BlitzkriegSymbols.S) {
                    tl.add(this.getSymbolNoWinAnimation(reelId, symId), 0);
                }
            });
            delay = delay + 0.1;
        });
        return tl;
    }

    private getSymbolWinAnimation(reelId: number, symId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const sym: DDaySymbol = DDay.reels[reelId].getSymbol(symId, false, false);
        let overlayTl = new TimelineLite();
        let fireOverlay = DDay.fireOverlays[reelId][symId];
        tl.add(() => DDay.reels[reelId].reelView.addRemoveWinSymbol(sym, true));// no need to have win state in this game?
        tl.add([overlayTl, DDay.commonService.dimFireOverlay(false, reelId, symId), DDay.commonService.dimBulletOverlay(false, reelId, symId), sym.getStateAnimation(BlitzKriegSymbolState.WIN, {
            duration: 0,
            alpha: 1
        }, {duration: 0})]);
        return tl;
    }

    private getSymbolNoWinAnimation(reelId: number, symId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const sym: DDaySymbol = DDay.reels[reelId].getSymbol(symId, false, false);
        tl.add([DDay.commonService.dimFireOverlay(true, reelId, symId), DDay.commonService.dimBulletOverlay(true, reelId, symId), sym.getStateAnimation(BlitzKriegSymbolState.NO_WIN, {
            duration: 0,
            alpha: 1
        }, {duration: 0})]);
        return tl;
    }

    private getEffectLevel(winRatio: number): number {
        return 0;
    }

    public async stop(): Promise<any> {
        this._stopped = true;
        if (this._currentTimeline && this._currentTimeline.isActive()) {
            await this._currentTimeline.progress(1);
        }
        this._currentTimeline = null;
        await Promise.resolve();
    }
}
